// src/pages/MenteeProfilePage.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BigFiveModal from '../components/BigFiveModal';
import { Camera } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

// GREENTECH_MATCHING_PREFS_UI_v1
// Greentech Program Matching Prefs (Survey 28)
// Fixe Auswahllisten, keine Auto-Vorschläge / kein Prefill.
const GREENTECH_SURVEY_ID = 28;
const GREENTECH_MAX_SELECTION = 3;

const GREENTECH_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
];

const GREENTECH_APPLICATION_FIELDS = [
  'Renewable Energy Systems',
  'Smart Grids & Flexibility',
  'Energy Markets & Regulation',
  'Green Hydrogen & Power-to-X',
  'Battery & Storage Technologies',
  'Sustainable Mobility & Public Transit',
  'Climate Risk, Flooding & Resilience',
  'Nature-based Solutions & Ecosystems',
  'Sustainable Water & Sanitation',
  'Circular Economy & Recycling',
  'Waste-to-Value & Biomass',
  'Precision Agriculture & Food Systems',
  'Earth Observation & Environmental Monitoring',
  'Green Finance & ESG / Anti-Greenwashing',
  'Sustainable Cities & Buildings',
  'Industrial Decarbonization',
];

const GREENTECH_METHODS_SKILLS = [
  'Remote Sensing',
  'GIS / Geospatial Analysis',
  'Machine Learning',
  'Data Science & Statistics',
  'Time-Series Forecasting',
  'Optimization / Operations Research',
  'System Modelling (Energy/Transport)',
  'Life Cycle Assessment (LCA)',
  'Techno-Economic Analysis (TEA)',
  'Computer Vision',
  'IoT / Sensor Systems',
  'Simulation (Agent-based, Monte Carlo)',
  'Policy & Impact Evaluation',
  'Laboratory / Microbiology Methods',
  'Bioinformatics / Genomics',
  'Robotics / Automation',
];

// Legacy → aktuelle Label (wichtig: exakt wie deine Chip-Labels)
const GREENTECH_LEGACY_MAP_APPLICATION_FIELDS = {
  'Nature-based Solutions': 'Nature-based Solutions & Ecosystems',
};

// Optional: falls alte Sprachwerte im Umlauf sind
const GREENTECH_LEGACY_MAP_LANGUAGES = {
  English: 'en',
  Deutsch: 'de',
};

const normalizeEnumArray = (arr, allowed, max, legacyMap = {}) => {
  const out = [];
  const src = Array.isArray(arr) ? arr : [];

  for (const raw of src) {
    const s0 = String(raw ?? '').trim();
    if (!s0) continue;

    const s = legacyMap[s0] ?? s0;
    if (!allowed.includes(s)) continue;

    if (!out.includes(s)) out.push(s);
    if (out.length >= max) break;
  }

  return out;
};

function MenteeProfilePage() {
  const { t, i18n } = useTranslation();
  const [firstTimeSaved, setFirstTimeSaved] = useState(false);
  const [showBigFiveModal, setShowBigFiveModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [bigfiveData, setBigfiveData] = useState({});

  // ✨ NEU: Merker, ob die Analyse schon erledigt ist
  const [analysisDone, setAnalysisDone] = useState(false);

  const [imageFile, setImageFile] = useState(null);
  const [avatarPath, setAvatarPath] = useState('');
  const [remoteImageBase64, setRemoteImageBase64] = useState(null);

  const [firstNameValue, setFirstNameValue] = useState('');
  const [lastNameValue, setLastNameValue] = useState('');
  const [mailValue, setMailValue] = useState('');
  const [phoneValue, setPhoneValue] = useState('');
  const [standortValue, setStandortValue] = useState('');
  const [abteilungValue, setAbteilungValue] = useState('');
  const [jobTitleValue, setJobTitleValue] = useState('');

  // ✅ ehem. Branchen-Know-how → jetzt (nur Survey 28) Freitext "Erfahrung in 2-3 Sätzen"
  const [branchenKnowValue, setBranchenKnowValue] = useState('');

  // ✅ neu: Branch separat halten (readOnly Anzeige für Nicht-28)
  const [branchNameValue, setBranchNameValue] = useState('');
  const [surveyId, setSurveyId] = useState(null);

  const [experienceValue, setExperienceValue] = useState('0-2 Jahre');
  const [goals, setGoals] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [whyMentoring, setWhyMentoring] = useState('');

  // GreenTech Matching Prefs (Survey 28)
  const [gtPrefsAvailable, setGtPrefsAvailable] = useState(true);
  const [gtPrefsLoading, setGtPrefsLoading] = useState(true);
  const [gtPrefsSaving, setGtPrefsSaving] = useState(false);
  const [gtPrefsMessage, setGtPrefsMessage] = useState('');
  const [gtPrefsError, setGtPrefsError] = useState('');

  // ✅ NEU: Merker ob Matching-Prefs wirklich gespeichert sind (persistiert)
  const [gtPrefsSavedOk, setGtPrefsSavedOk] = useState(false);

  const [gtLanguages, setGtLanguages] = useState([]);
  const [gtMentorPreference, setGtMentorPreference] = useState(null); // 'science' | 'industry' | 'both'

  // ✅ NEU: mentor_background ist Pflichtfeld (science | industry) – auch für Mentee
  const [gtMentorBackground, setGtMentorBackground] = useState(null); // 'science' | 'industry'

  const [gtApplicationFields, setGtApplicationFields] = useState([]);
  const [gtMethodsSkills, setGtMethodsSkills] = useState([]);

  const toggleInList = (value, list, setList, max = null) => {
    if (!value) return;

    // ✅ NEU: Sobald geändert wird: "gespeichert"-Status zurücksetzen + Hinweise leeren
    setGtPrefsSavedOk(false);
    setGtPrefsMessage('');
    setGtPrefsError('');

    if (list.includes(value)) {
      setList(list.filter(v => v !== value));
      return;
    }
    if (typeof max === 'number' && max > 0 && list.length >= max) {
      return; // hard cap
    }
    setList([...list, value]);
  };

  // Pflichtfeld-Texte (DE/EN) ohne neue i18n-Keys
  const isGerman = String(i18n?.language || '').toLowerCase().startsWith('de');
  const tr = (de, en) => (isGerman ? de : en);

  // Übersetzbar machen
  const devGoals = t('menteeProfile.devGoals', { returnObjects: true });
  const competenceData = t('menteeProfile.competenceData', { returnObjects: true });

  const navigate = useNavigate();

  // ✅ NEU: Onboarding-Milestone setzen, sobald Profilseite erreicht ist
  useEffect(() => {
    axios
      .post('/mentoring/onboarding/complete', null, { withCredentials: true })
      .catch(() => {
        // best effort – absichtlich still
      });
  }, []);

  useEffect(() => {
    axios.get('/mentoring/mentee/profile', { withCredentials: true }).then((res) => {
      if (res.data.avatarPath) setAvatarPath(res.data.avatarPath.replace(/\\/g, '/'));
      if (res.data.avatar) setRemoteImageBase64(`data:image/png;base64,${res.data.avatar}`);
      if (res.data.email) setMailValue(res.data.email);
      if (res.data.firstName) setFirstNameValue(res.data.firstName);
      if (res.data.lastName) setLastNameValue(res.data.lastName);
      if (res.data.phone) setPhoneValue(res.data.phone);
      if (res.data.standort) setStandortValue(res.data.standort);
      if (res.data.abteilung) setAbteilungValue(res.data.abteilung);
      if (res.data.jobTitle) setJobTitleValue(res.data.jobTitle);

      // ✅ NEU: survey_id + branchName separat speichern
      if (typeof res.data.survey_id !== 'undefined' && res.data.survey_id !== null) {
        const sid = Number(res.data.survey_id);
        setSurveyId(Number.isFinite(sid) ? sid : null);
      } else {
        setSurveyId(null);
      }

      if (typeof res.data.branchName === 'string') {
        setBranchNameValue(res.data.branchName);
      } else {
        setBranchNameValue('');
      }

      // ✅ FIX: Beschreibung wirklich aus API übernehmen (nur Survey 28)
      const sidNum = Number(res.data.survey_id);
      if (sidNum === GREENTECH_SURVEY_ID) {
        if (typeof res.data.profileDescription === 'string') {
          setBranchenKnowValue(res.data.profileDescription);
        } else {
          setBranchenKnowValue('');
        }
      } else {
        // Für alle anderen Surveys bleibt Beschreibung leer (Branch wird separat readOnly angezeigt)
        setBranchenKnowValue('');
      }

      if (res.data.experience) setExperienceValue(res.data.experience);
      if (Array.isArray(res.data.goals)) {
        setGoals((prev) => (Array.isArray(prev) && prev.length > 0 ? prev : res.data.goals));
      }

      if (Array.isArray(res.data.competences)) {
        setCompetences((prev) =>
          Array.isArray(prev) && prev.length > 0 ? prev : res.data.competences
        );
      }
      if (res.data.whyMentoring) setWhyMentoring(res.data.whyMentoring);
      if (res.data.user_id) setUserId(res.data.user_id);

      if (res.data.bigfive_extraversion) {
        // ✅ Guarded-Set: vorhandene Werte beibehalten, fehlende nicht überschreiben
        setBigfiveData((prev = {}) => ({
          extraversion: res.data.bigfive_extraversion ?? prev.extraversion ?? null,
          gewissenhaftigkeit: res.data.bigfive_gewissenhaftigkeit ?? prev.gewissenhaftigkeit ?? null,
          negative_emotionalitaet: res.data.bigfive_negative_emotionalitaet ?? prev.negative_emotionalitaet ?? null,
          vertraeglichkeit: res.data.bigfive_vertraeglichkeit ?? prev.vertraeglichkeit ?? null,
          offenheit: res.data.bigfive_offenheit ?? prev.offenheit ?? null,
          extraversion_text: res.data.bigfive_extraversion_text ?? prev.extraversion_text ?? '',
          gewissenhaftigkeit_text: res.data.bigfive_gewissenhaftigkeit_text ?? prev.gewissenhaftigkeit_text ?? '',
          negative_emotionalitaet_text: res.data.bigfive_negative_emotionalitaet_text ?? prev.negative_emotionalitaet_text ?? '',
          vertraeglichkeit_text: res.data.bigfive_vertraeglichkeit_text ?? prev.vertraeglichkeit_text ?? '',
          offenheit_text: res.data.bigfive_offenheit_text ?? prev.offenheit_text ?? ''
        }));
      }

      // ✨ NEU: Analyse-Status aus der API übernehmen
      if (res.data.bigfive_updated_at) {
        setAnalysisDone(true);
      } else {
        setAnalysisDone(false);
      }
    }).catch(console.error);
  }, []);

  // GreenTech Matching Prefs laden (Survey 28)
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setGtPrefsLoading(true);
      setGtPrefsMessage('');
      setGtPrefsError('');

      try {
        const r = await axios.get(`/mentoring/survey-matching-prefs/${GREENTECH_SURVEY_ID}`, {
          withCredentials: true,
        });

        if (cancelled) return;

        const d = r?.data || {};
        setGtPrefsAvailable(true);

        // Nur gespeicherte Werte anzeigen (kein Prefill aus Profil) + Normalisierung (Legacy → aktuelle Labels)
        const allowedLangs = GREENTECH_LANGUAGE_OPTIONS.map(o => o.value);

        const savedLanguages = normalizeEnumArray(d.languages, allowedLangs, 10, GREENTECH_LEGACY_MAP_LANGUAGES);
        const savedMentorPreference = d.mentor_preference ?? null;
        const savedMentorBackground = d.mentor_background ?? null;

        const savedApplicationFields = normalizeEnumArray(
          d.application_fields,
          GREENTECH_APPLICATION_FIELDS,
          GREENTECH_MAX_SELECTION,
          GREENTECH_LEGACY_MAP_APPLICATION_FIELDS
        );
        const savedMethodsSkills = normalizeEnumArray(
          d.methods_skills,
          GREENTECH_METHODS_SKILLS,
          GREENTECH_MAX_SELECTION
        );

        setGtLanguages(savedLanguages);
        setGtMentorPreference(savedMentorPreference);

        // ✅ NEU: mentor_background aus API übernehmen
        setGtMentorBackground(savedMentorBackground);

        setGtApplicationFields(savedApplicationFields);
        setGtMethodsSkills(savedMethodsSkills);

        // ✅ NEU: "persistiert gespeichert" Status merken
        const savedOk =
          Array.isArray(savedLanguages) && savedLanguages.length > 0 &&
          !!savedMentorPreference &&
          !!savedMentorBackground &&
          Array.isArray(savedApplicationFields) && savedApplicationFields.length > 0 &&
          Array.isArray(savedMethodsSkills) && savedMethodsSkills.length > 0;

        setGtPrefsSavedOk(savedOk);
      } catch (e) {
        if (cancelled) return;

        const status = e?.response?.status;
        const err = e?.response?.data?.error;

        // Für andere Kunden/Surveys soll der Block einfach nicht erscheinen
        if (status === 403 && (err === 'invalid_tenant_survey' || err === 'forbidden')) {
          setGtPrefsAvailable(false);
          return;
        }

        setGtPrefsAvailable(true);
        setGtPrefsError(err || 'failed_to_load');
      } finally {
        if (!cancelled) setGtPrefsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveGreenTechPrefs = async () => {
    setGtPrefsMessage('');
    setGtPrefsError('');

    if (!Array.isArray(gtLanguages) || gtLanguages.length === 0) {
      setGtPrefsError(t('menteeProfile.gtPrefs.errorSelectLanguage'));
      return;
    }
    if (!gtMentorPreference) {
      setGtPrefsError(t('menteeProfile.gtPrefs.errorSelectMentorType'));
      return;
    }

    // ✅ NEU: mentor_background Pflichtfeld
    if (!gtMentorBackground) {
      setGtPrefsError(tr(
        'Bitte deinen Hintergrund auswählen (Science oder Industry).',
        'Please select your background (science or industry).'
      ));
      return;
    }

    // ✅ NEU: Pflichtfelder (Anwendungsfelder + Methoden/Skills)
    if (!Array.isArray(gtApplicationFields) || gtApplicationFields.length === 0) {
      setGtPrefsError(tr(
        'Bitte mindestens ein Anwendungsfeld auswählen.',
        'Please select at least one application field.'
      ));
      return;
    }
    if (!Array.isArray(gtMethodsSkills) || gtMethodsSkills.length === 0) {
      setGtPrefsError(tr(
        'Bitte mindestens eine Methode / Skill auswählen.',
        'Please select at least one method / skill.'
      ));
      return;
    }

    const allowedLangs = GREENTECH_LANGUAGE_OPTIONS.map(o => o.value);

    const payload = {
      languages: normalizeEnumArray(gtLanguages, allowedLangs, 10, GREENTECH_LEGACY_MAP_LANGUAGES),
      mentor_preference: gtMentorPreference,

      // ✅ NEU: mentor_background mitschicken
      mentor_background: gtMentorBackground,

      application_fields: normalizeEnumArray(
        gtApplicationFields,
        GREENTECH_APPLICATION_FIELDS,
        GREENTECH_MAX_SELECTION,
        GREENTECH_LEGACY_MAP_APPLICATION_FIELDS
      ),
      methods_skills: normalizeEnumArray(gtMethodsSkills, GREENTECH_METHODS_SKILLS, GREENTECH_MAX_SELECTION),
    };

    try {
      setGtPrefsSaving(true);
      const r = await axios.put(`/mentoring/survey-matching-prefs/${GREENTECH_SURVEY_ID}`, payload, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      });

      if (r?.data?.success) {
        setGtPrefsMessage(t('menteeProfile.gtPrefs.saved'));

        // ✅ NEU: nach erfolgreichem Speichern "persistiert ok"
        setGtPrefsSavedOk(true);
      } else {
        setGtPrefsError(r?.data?.error || 'save_failed');
      }
    } catch (e) {
      const err = e?.response?.data?.error || 'save_failed';
      setGtPrefsError(err);
    } finally {
      setGtPrefsSaving(false);
    }
  };

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    axios.post('/mentoring/upload/image', formData, {
      withCredentials: true
    })
      .then((res) => {
        setAvatarPath(res.data.filePath.replace(/^uploads[\\/]+/, ''));
        // setImageFile(file);
      }).catch(console.error);
  }

  // MAX 2 Entwicklungsziele auswählbar
  function handleDevGoalChange(goal) {
    if (goals.includes(goal)) {
      setGoals(goals.filter(g => g !== goal));
    } else if (goals.length < 2) {
      setGoals([...goals, goal]);
    }
  }

  // MAX 3 Kompetenzen auswählbar
  function handleCompetenceChange(item) {
    if (competences.includes(item)) {
      setCompetences(competences.filter(c => c !== item));
    } else if (competences.length < 3) {
      setCompetences([...competences, item]);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    // ✅ NEU: Pflichtfelder (Basis-Profil)
    if (!firstNameValue || String(firstNameValue).trim().length === 0) {
      alert(tr('Bitte Vorname ausfüllen.', 'Please enter your first name.'));
      return;
    }
    if (!lastNameValue || String(lastNameValue).trim().length === 0) {
      alert(tr('Bitte Nachname ausfüllen.', 'Please enter your last name.'));
      return;
    }
    if (!jobTitleValue || String(jobTitleValue).trim().length === 0) {
      alert(tr('Bitte Position / Jobtitel ausfüllen.', 'Please enter your position / job title.'));
      return;
    }
    if (!branchenKnowValue || String(branchenKnowValue).trim().length === 0) {
      alert(tr('Bitte beschreibe deine Erfahrung (2–3 Sätze).', 'Please describe your experience (2–3 sentences).'));
      return;
    }
    if (!experienceValue || String(experienceValue).trim().length === 0) {
      alert(tr('Bitte Dauer der Berufserfahrung auswählen.', 'Please select your years of professional experience.'));
      return;
    }

    // ✅ NEU: Pflichtfelder (Entwicklungsziele + Kompetenzfelder)
    if (!Array.isArray(goals) || goals.length === 0) {
      alert(tr(
        'Bitte mindestens ein Entwicklungsziel auswählen.',
        'Please select at least one development goal.'
      ));
      return;
    }
    if (!Array.isArray(competences) || competences.length === 0) {
      alert(tr(
        'Bitte mindestens ein Kompetenzfeld auswählen.',
        'Please select at least one competence.'
      ));
      return;
    }

    const isSurvey28 = surveyId === GREENTECH_SURVEY_ID;

    // ✅ NEU: Matching-Präferenzen sind Pflicht – aber nur im Survey 28
    // -> wir prüfen den gespeicherten Stand im Backend (nicht nur lokale UI-State),
    //    damit BigFive wirklich erst nach "Prefs speichern" möglich ist.
    if (isSurvey28 && gtPrefsAvailable) {
      try {
        const r = await axios.get(`/mentoring/survey-matching-prefs/${GREENTECH_SURVEY_ID}`, {
          withCredentials: true,
        });

        const d = r?.data || {};
        const allowedLangs = GREENTECH_LANGUAGE_OPTIONS.map(o => o.value);

        const savedLanguages = normalizeEnumArray(d.languages, allowedLangs, 10, GREENTECH_LEGACY_MAP_LANGUAGES);
        const savedMentorPreference = d.mentor_preference ?? null;
        const savedMentorBackground = d.mentor_background ?? null;
        const savedApplicationFields = normalizeEnumArray(
          d.application_fields,
          GREENTECH_APPLICATION_FIELDS,
          GREENTECH_MAX_SELECTION,
          GREENTECH_LEGACY_MAP_APPLICATION_FIELDS
        );
        const savedMethodsSkills = normalizeEnumArray(
          d.methods_skills,
          GREENTECH_METHODS_SKILLS,
          GREENTECH_MAX_SELECTION
        );

        if (!Array.isArray(savedLanguages) || savedLanguages.length === 0) {
          alert(tr(
            'Bitte zuerst deine Matching-Präferenzen speichern (Sprache fehlt).',
            'Please save your matching preferences first (language is missing).'
          ));
          return;
        }
        if (!savedMentorPreference) {
          alert(tr(
            'Bitte zuerst deine Matching-Präferenzen speichern (Mentor-Typ fehlt).',
            'Please save your matching preferences first (mentor type is missing).'
          ));
          return;
        }
        if (!savedMentorBackground) {
          alert(tr(
            'Bitte zuerst deine Matching-Präferenzen speichern (Hintergrund fehlt).',
            'Please save your matching preferences first (background is missing).'
          ));
          return;
        }
        if (!Array.isArray(savedApplicationFields) || savedApplicationFields.length === 0) {
          alert(tr(
            'Bitte zuerst deine Matching-Präferenzen speichern (Anwendungsfelder fehlen).',
            'Please save your matching preferences first (application fields are missing).'
          ));
          return;
        }
        if (!Array.isArray(savedMethodsSkills) || savedMethodsSkills.length === 0) {
          alert(tr(
            'Bitte zuerst deine Matching-Präferenzen speichern (Methoden/Skills fehlen).',
            'Please save your matching preferences first (methods/skills are missing).'
          ));
          return;
        }
      } catch (err) {
        console.error(err);
        alert(tr(
          'Matching-Präferenzen konnten nicht geprüft werden. Bitte kurz neu laden und erneut versuchen.',
          'Could not verify matching preferences. Please reload and try again.'
        ));
        return;
      }
    }

    const formData = new FormData();
    formData.append('avatarPath', avatarPath);
    formData.append('firstName', firstNameValue);
    formData.append('lastName', lastNameValue);
    formData.append('email', mailValue);
    formData.append('phone', phoneValue);
    formData.append('standort', standortValue);
    formData.append('abteilung', abteilungValue);
    formData.append('jobTitle', jobTitleValue);

    // ✅ bleibt vom Backend her gleich: wir nutzen denselben Key weiter
    // (für survey_id=28 wird das als Beschreibung gespeichert)
    formData.append('branchenKnow', branchenKnowValue);

    formData.append('experience', experienceValue);
    formData.append('goals', JSON.stringify(goals));
    formData.append('competences', JSON.stringify(competences));
    formData.append('whyMentoring', whyMentoring);

    try {
      await axios.post('/mentoring/mentee/profile', formData, {
        withCredentials: true
      });
      alert(t('menteeProfile.saveSuccess'));

      if (!firstTimeSaved) setFirstTimeSaved(true);
      if (!analysisDone) setShowBigFiveModal(true);

    } catch (err) {
      console.error(err);
      alert(t('menteeProfile.saveError'));
    }
  }

  const displayedImage = avatarPath
    ? `/mentoring/uploads/${avatarPath}`           // relativ -> geht auf app.menvio.ai über NGINX
    : (remoteImageBase64 || null);

  const isSurvey28 = surveyId === GREENTECH_SURVEY_ID;

  const gtPrefsAllRequiredOk =
    !isSurvey28 || !gtPrefsAvailable
      ? true
      : (
        Array.isArray(gtLanguages) && gtLanguages.length > 0 &&
        !!gtMentorPreference &&
        !!gtMentorBackground &&
        Array.isArray(gtApplicationFields) && gtApplicationFields.length > 0 &&
        Array.isArray(gtMethodsSkills) && gtMethodsSkills.length > 0
      );

  // ✅ NEU: Profil-Button wie Matching-Prefs deaktivieren + Hinweis anzeigen
  const profileRequiredOk =
    !!String(firstNameValue || '').trim() &&
    !!String(lastNameValue || '').trim() &&
    !!String(jobTitleValue || '').trim() &&
    !!String(branchenKnowValue || '').trim() &&
    !!String(experienceValue || '').trim() &&
    Array.isArray(goals) && goals.length > 0 &&
    Array.isArray(competences) && competences.length > 0;

  const canSaveProfile =
    profileRequiredOk &&
    (!isSurvey28 || !gtPrefsAvailable ? true : gtPrefsSavedOk);

  return (
    <div className="min-h-screen bg-black text-white p-6">
      {/* Zurück-Button */}
      <div className="flex justify-start mb-6">
        <button
          type="button"
          onClick={() => navigate('/mentee-dashboard')}
          className="flex items-center gap-2 bg-mentixxgreen text-black font-bold py-2 px-4 rounded-2xl hover:bg-green-400 transition"
        >
          <span className="text-xl">←</span> {t('menteeProfile.backToDashboard')}
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-6">{t('menteeProfile.heading')}</h2>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Profilbild-Kachel */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow flex flex-col items-center">
          <div className="relative w-32 h-32">
            {displayedImage ? (
              <img
                src={displayedImage}
                alt={t('menteeProfile.avatarAlt')}
                className="w-32 h-32 rounded-full object-cover shadow-md"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-700 flex items-center justify-center text-gray-400">
                {t('menteeProfile.noAvatar')}
              </div>
            )}
            <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-60 rounded-full cursor-pointer">
              <Camera className="w-8 h-8 text-white opacity-0 hover:opacity-100" />
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>
          <p className="text-sm text-gray-400 mt-2">{t('menteeProfile.changeAvatar')}</p>
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <label>
            {t('menteeProfile.firstName')} <span className="text-red-400">*</span>
          </label>
          <input type="text" value={firstNameValue} onChange={(e) => setFirstNameValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>
            {t('menteeProfile.lastName')} <span className="text-red-400">*</span>
          </label>
          <input type="text" value={lastNameValue} onChange={(e) => setLastNameValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('menteeProfile.email')}</label>
          <input type="email" value={mailValue} readOnly className="w-full p-2 rounded bg-gray-700" />
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <label>{t('menteeProfile.phone')}</label>
          <input type="text" value={phoneValue} onChange={(e) => setPhoneValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('menteeProfile.location')}</label>
          <input type="text" value={standortValue} onChange={(e) => setStandortValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
          <label>{t('menteeProfile.department')}</label>
          <select value={abteilungValue} onChange={(e) => setAbteilungValue(e.target.value)} className="w-full p-2 rounded bg-gray-700">
            {t('menteeProfile.departmentOptions', { returnObjects: true }).map((opt) => (
              <option key={opt}>{opt}</option>
            ))}
          </select>
          <label>
            {t('menteeProfile.jobTitle')} <span className="text-red-400">*</span>
          </label>
          <input type="text" value={jobTitleValue} onChange={(e) => setJobTitleValue(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          {isSurvey28 ? (
            <>
              <label>
                {tr(
                  'Beschreibe Deine Erfahrung kurz in 2-3 Sätzen',
                  'Describe your experience briefly in 2–3 sentences'
                )}{' '}
                <span className="text-red-400">*</span>
              </label>
              <textarea
                value={branchenKnowValue}
                onChange={(e) => setBranchenKnowValue(e.target.value)}
                className="w-full p-2 rounded bg-gray-700"
                rows={3}
                placeholder={tr(
                  'Z. B. Schwerpunkte, Domänen, typische Projekte…',
                  'E.g., focus areas, domains, typical projects…'
                )}
              />
            </>
          ) : (
            <>
              <label>{t('menteeProfile.branchKnow')}</label>
              <textarea
                value={branchNameValue}
                readOnly
                className="w-full p-2 rounded bg-gray-700"
                rows={3}
              />
            </>
          )}

          <label>
            {t('menteeProfile.experience')} <span className="text-red-400">*</span>
          </label>
          <select value={experienceValue} onChange={(e) => setExperienceValue(e.target.value)} className="w-full p-2 rounded bg-gray-700">
            {t('menteeProfile.experienceOptions', { returnObjects: true }).map((opt) => (
              <option key={opt}>{opt}</option>
            ))}
          </select>
        </div>

        <div className="md:grid md:grid-cols-2 md:gap-6 bg-gray-800 p-6 rounded-2xl shadow md:col-span-2">
          <div>
            <p className="text-lg font-semibold mb-2">
              {t('menteeProfile.goalsLabel')} <span className="text-red-400">*</span>
              <span className="text-xs text-gray-400">({t('menteeProfile.goalsMax')})</span>
            </p>
            {devGoals.map((goal) => (
              <div key={goal} className="flex items-center mb-1">
                <input
                  type="checkbox"
                  checked={goals.includes(goal)}
                  onChange={() => handleDevGoalChange(goal)}
                  disabled={!goals.includes(goal) && goals.length >= 2}
                  className="mr-2"
                />
                <label>{goal}</label>
              </div>
            ))}
            {goals.length >= 2 && (
              <div className="text-xs text-mentixxgreen mt-1">{t('menteeProfile.goalsLimit')}</div>
            )}
          </div>

          <div>
            <p className="text-lg font-semibold mb-2">
              {t('menteeProfile.competencesLabel')} <span className="text-red-400">*</span>
              <span className="text-xs text-gray-400">({t('menteeProfile.competencesMax')})</span>
            </p>
            {competenceData.map((cat) => (
              <div key={cat.kategorie} className="mb-6">
                <strong className="block mb-2">{cat.kategorie}</strong>
                <div className="grid grid-cols-2 gap-2 ml-4">
                  {cat.items.map((item) => (
                    <div key={item} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={competences.includes(item)}
                        onChange={() => handleCompetenceChange(item)}
                        disabled={!competences.includes(item) && competences.length >= 3}
                        className="mr-2"
                      />
                      <label>{item}</label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {competences.length >= 3 && (
              <div className="text-xs text-mentixxgreen mt-1">{t('menteeProfile.competencesLimit')}</div>
            )}
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-2xl shadow md:col-span-2">
          <label>{t('menteeProfile.whyMentoring')}</label>
          <textarea value={whyMentoring} onChange={(e) => setWhyMentoring(e.target.value)} className="w-full p-2 rounded bg-gray-700" />
        </div>

        {/* GreenTech Matching Prefs (nur sichtbar, wenn Survey 28 für den Tenant erlaubt ist) */}
        {gtPrefsAvailable && (
          <div className="md:col-span-2 border border-gray-700 rounded-xl p-4 bg-gray-900/40">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">{t('menteeProfile.gtPrefs.title')}</h3>
                <p className="text-sm text-gray-300 mt-1">
                  <Trans
                    i18nKey="menteeProfile.gtPrefs.description"
                    components={{ strong: <strong className="font-semibold" /> }}
                  />
                </p>
              </div>
              {gtPrefsLoading && <div className="text-sm text-gray-400">{t('menteeProfile.gtPrefs.loading')}</div>}
            </div>

            {/* Sprache */}
            <div className="mt-4">
              <p className="text-sm font-semibold">
                {t('menteeProfile.gtPrefs.languageTitle')} <span className="text-red-400">*</span>
              </p>
              <div className="flex flex-wrap gap-3 mt-2">
                {GREENTECH_LANGUAGE_OPTIONS.map(opt => (
                  <label key={opt.value} className="inline-flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={gtLanguages.includes(opt.value)}
                      onChange={() => toggleInList(opt.value, gtLanguages, setGtLanguages)}
                    />
                    <span>
                      {t(`menteeProfile.gtPrefs.languageLabel.${opt.value}`, { defaultValue: opt.label })}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Mentor-Typ */}
            <div className="mt-4">
              <p className="text-sm font-semibold">
                {t('menteeProfile.gtPrefs.mentorTypeTitle')} <span className="text-red-400">*</span>
              </p>
              <div className="flex flex-wrap gap-4 mt-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="gtMentorPref"
                    value="science"
                    checked={gtMentorPreference === 'science'}
                    onChange={() => {
                      setGtMentorPreference('science');
                      setGtPrefsSavedOk(false);
                      setGtPrefsMessage('');
                      setGtPrefsError('');
                    }}
                  />
                  <span>{t('menteeProfile.gtPrefs.mentorType.science')}</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="gtMentorPref"
                    value="industry"
                    checked={gtMentorPreference === 'industry'}
                    onChange={() => {
                      setGtMentorPreference('industry');
                      setGtPrefsSavedOk(false);
                      setGtPrefsMessage('');
                      setGtPrefsError('');
                    }}
                  />
                  <span>{t('menteeProfile.gtPrefs.mentorType.industry')}</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="gtMentorPref"
                    value="both"
                    checked={gtMentorPreference === 'both'}
                    onChange={() => {
                      setGtMentorPreference('both');
                      setGtPrefsSavedOk(false);
                      setGtPrefsMessage('');
                      setGtPrefsError('');
                    }}
                  />
                  <span>{t('menteeProfile.gtPrefs.mentorType.both')}</span>
                </label>
              </div>
            </div>

            {/* ✅ NEU: Background (Pflichtfeld) */}
            <div className="mt-4">
              <p className="text-sm font-semibold">
                {tr('Dein Hintergrund', 'Your background')} <span className="text-red-400">*</span>
              </p>
              <div className="flex flex-wrap gap-4 mt-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="gtMentorBackground"
                    value="science"
                    checked={gtMentorBackground === 'science'}
                    onChange={() => {
                      setGtMentorBackground('science');
                      setGtPrefsSavedOk(false);
                      setGtPrefsMessage('');
                      setGtPrefsError('');
                    }}
                  />
                  <span>{tr('Science / Forschung', 'Science / Research')}</span>
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="gtMentorBackground"
                    value="industry"
                    checked={gtMentorBackground === 'industry'}
                    onChange={() => {
                      setGtMentorBackground('industry');
                      setGtPrefsSavedOk(false);
                      setGtPrefsMessage('');
                      setGtPrefsError('');
                    }}
                  />
                  <span>{tr('Industry / Wirtschaft', 'Industry')}</span>
                </label>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-6">
              {/* Application fields */}
              <div>
                <p className="text-sm font-semibold">
                  {t('menteeProfile.gtPrefs.applicationFieldsTitle', { max: GREENTECH_MAX_SELECTION })}{' '}
                  <span className="text-red-400">*</span>
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {GREENTECH_APPLICATION_FIELDS.map(item => {
                    const active = gtApplicationFields.includes(item);
                    const disabled = !active && gtApplicationFields.length >= GREENTECH_MAX_SELECTION;
                    return (
                      <button
                        type="button"
                        key={item}
                        disabled={disabled}
                        onClick={() => toggleInList(item, gtApplicationFields, setGtApplicationFields, GREENTECH_MAX_SELECTION)}
                        className={`text-xs px-3 py-1 rounded-full border ${active ? 'bg-mentixxgreen text-black border-mentixxgreen' : 'border-gray-600 text-gray-200'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Methods & skills */}
              <div>
                <p className="text-sm font-semibold">
                  {t('menteeProfile.gtPrefs.methodsSkillsTitle', { max: GREENTECH_MAX_SELECTION })}{' '}
                  <span className="text-red-400">*</span>
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {GREENTECH_METHODS_SKILLS.map(item => {
                    const active = gtMethodsSkills.includes(item);
                    const disabled = !active && gtMethodsSkills.length >= GREENTECH_MAX_SELECTION;
                    return (
                      <button
                        type="button"
                        key={item}
                        disabled={disabled}
                        onClick={() => toggleInList(item, gtMethodsSkills, setGtMethodsSkills, GREENTECH_MAX_SELECTION)}
                        className={`text-xs px-3 py-1 rounded-full border ${active ? 'bg-mentixxgreen text-black border-mentixxgreen' : 'border-gray-600 text-gray-200'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {(gtPrefsError || gtPrefsMessage) && (
              <div className="mt-4 text-sm">
                {gtPrefsError && <div className="text-red-300">{gtPrefsError}</div>}
                {!gtPrefsError && gtPrefsMessage && <div className="text-green-300">{gtPrefsMessage}</div>}
              </div>
            )}

            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={saveGreenTechPrefs}
                disabled={gtPrefsSaving || gtPrefsLoading || !gtPrefsAllRequiredOk}
                className={`bg-mentixxgreen text-black font-bold py-2 px-4 rounded ${
                  (gtPrefsSaving || gtPrefsLoading || !gtPrefsAllRequiredOk) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {gtPrefsSaving ? t('menteeProfile.gtPrefs.saving') : t('menteeProfile.gtPrefs.saveButton')}
              </button>

              <span className="text-xs text-gray-400">
                {t('menteeProfile.gtPrefs.appliesToSurvey', { surveyId: GREENTECH_SURVEY_ID })}
              </span>
            </div>
          </div>
        )}

        {!canSaveProfile && (
          <div className="md:col-span-2 text-sm border border-yellow-700 bg-yellow-900/20 text-yellow-200 rounded-xl p-3">
            {!profileRequiredOk ? (
              <span>
                {tr(
                  'Bitte fülle alle Pflichtfelder (*) aus, bevor du das Profil speichern kannst.',
                  'Please fill all required fields (*) before saving your profile.'
                )}
              </span>
            ) : (
              <span>
                {tr(
                  'Bitte speichere zuerst deine Matching-Präferenzen (Button oben), damit du dein Profil speichern kannst.',
                  'Please save your matching preferences first (button above) before you can save your profile.'
                )}
              </span>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSaveProfile}
          className={`w-full py-2 rounded font-bold md:col-span-2 ${
            canSaveProfile ? 'bg-mentixxgreen text-black' : 'bg-mentixxgreen text-black opacity-50 cursor-not-allowed'
          }`}
        >
          {tr('Profil speichern / MentorMatch-Analyse', 'Save profile / MentorMatch analysis')}
        </button>
      </form>

      {/* Big Five Analyse */}
      {bigfiveData.extraversion && (
        <div className="mt-10 flex justify-center">
          <div className="bg-[#23272a] rounded-3xl shadow-lg p-8 max-w-xl w-full mx-auto">
            <div className="text-2xl font-bold text-center mb-6 text-white tracking-tight">{t('menteeProfile.bigFiveTitle')}</div>
            <div className="space-y-7 mb-5">
              {t('menteeProfile.bigFiveTraits', { returnObjects: true }).map((trait) => {
                const raw = parseFloat(bigfiveData[trait.key]) || 0;
                const percent = Math.round((raw / 5) * 100);
                return (
                  <div key={trait.key}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-semibold" style={{ color: trait.color }}>{trait.label}</span>
                      <span className="text-xs text-gray-300">{percent} / 100</span>
                    </div>
                    <div className="relative flex items-center h-6 mb-1">
                      <span className="absolute left-0 text-xs text-gray-400 select-none" title={trait.min}>0</span>
                      <span className="absolute right-0 text-xs text-gray-400 select-none" title={trait.max}>100</span>
                      <div className="absolute left-0 w-full h-3 bg-gray-900 rounded-full" />
                      <div
                        className="absolute left-0 h-3 rounded-full"
                        style={{
                          width: `${percent}%`,
                          background: `linear-gradient(90deg, ${trait.color} 70%, #23272a 100%)`
                        }}
                        title={percent < 34 ? trait.min : percent < 67 ? trait.mid : trait.max}
                      />
                      <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-6 bg-gray-600 rounded opacity-60" title={trait.mid}></div>
                    </div>
                    <div className="text-xs text-gray-400 text-center mt-0.5">{`50 / 100: ${trait.mid}`}</div>
                  </div>
                );
              })}
            </div>
            <div className="bg-[#1a1b1f] rounded-xl p-5 shadow-inner mt-7 space-y-3 text-[15px] leading-relaxed">
              {/* ✅ Anzeige-Fallback "—" für leere/nicht geladene Texte */}
              <div><b>{t('menteeProfile.traitExtraversion')}:</b> {bigfiveData.extraversion_text || '—'}</div>
              <div><b>{t('menteeProfile.traitConscientiousness')}:</b> {bigfiveData.gewissenhaftigkeit_text || '—'}</div>
              <div><b>{t('menteeProfile.traitEmotionalStability')}:</b> {bigfiveData.negative_emotionalitaet_text || '—'}</div>
              <div><b>{t('menteeProfile.traitAgreeableness')}:</b> {bigfiveData.vertraeglichkeit_text || '—'}</div>
              <div><b>{t('menteeProfile.traitOpenness')}:</b> {bigfiveData.offenheit_text || '—'}</div>
            </div>
          </div>
        </div>
      )}

      <BigFiveModal
        visible={showBigFiveModal}
        onClose={() => setShowBigFiveModal(false)}
        onStartTest={async () => {
          setShowBigFiveModal(false);
          const newWindow = window.open('about:blank', '_blank');

          const urlLang =
            new URLSearchParams(window.location.search).get('lang') ||
            (typeof i18n?.language === 'string' ? i18n.language : 'de');
          const lang = String(urlLang).slice(0, 2);

          // Auth-User-ID holen (nicht mentoring_user_id)
          const who = await fetch('/whoami', { credentials: 'include' })
            .then(r => r.json())
            .catch(() => null);

          const authUserId = who?.user?.id;
          if (!authUserId) {
            if (newWindow) newWindow.close();
            alert('Login fehlt oder Session abgelaufen. Bitte neu einloggen.');
            return;
          }

          const url = `/bigfive/app/?user_id=${authUserId}&lng=${lang}`;
          if (newWindow) {
            newWindow.location.href = url;
          } else {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }}
      />
    </div>
  );
}

export default MenteeProfilePage;
